import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { Aggregations, Filter, FilterTypes } from '@cbre/components';
import { FormControl } from '@angular/forms';
import { catchError, map, Observable, of } from 'rxjs';
import { PropertySetsState, PropertySetView } from '../../property-sets-dialog/property-sets-dialog';
import { STATE_PROVIDER, StateProvider } from '../../services/state-provider/state-provider';
import { PropertySetsHttpService } from '../../services/http/property-sets-http.service';
import { CATALOG_KNOWN_FILTERS, getFilters } from './property-set-filters.config';
import { getPropertyTypeLabel } from '@miq/utils';

@Component({
  selector: 'miq-property-set-filters',
  template: `
    <!-- filters -->
    <cbre-filters [filters]="filters" [aggregations]="aggregations" (valueChanges)="handleValueChanges($event)">
    </cbre-filters>
    <div class="fx--border__bottom fx--mt-20  fx--mb-12"></div>
    <div class="fx--h fx--ali-center ps-search-bar-wrapper">
      <div class="fx--column-5">
        <mat-form-field appearance="outline" class="miq-form-input">
          <input
            matInput
            [formControl]="searchCtrl"
            type="text"
            placeholder="Search Property Sets"
            (keydown.enter)="handleSearchChange($event)"
          />
          <div class="ps-search-bar__input-suffix" matSuffix>
            <mat-icon class="ps-search-bar__clear-icon" *ngIf="searchCtrl.value" (click)="this.searchCtrl.setValue('')">
              clear
            </mat-icon>
            <mat-icon class="ps-search-bar__icon">search</mat-icon>
          </div>
        </mat-form-field>
      </div>
      <div class="fx--column-5 fx--pl-md">
        <button mat-button color="accent" (click)="openCreateView()">
          <mat-icon class="playlist_add" fontIcon="playlist_add"></mat-icon>
          <span>{{ 'New Property Set' | uppercase }}</span>
        </button>
      </div>
    </div>
  `,
  styleUrls: ['./property-set-filters.component.scss'],
  standalone: false,
})
export class PropertySetFiltersComponent implements OnInit {
  public filters: Filter[] = [];

  @Output() filterChange = new EventEmitter<any>();

  public searchCtrl = new FormControl<string>(null, { updateOn: 'blur' });

  aggregations: Aggregations;

  constructor(
    @Inject(STATE_PROVIDER) private stateProvider: StateProvider<PropertySetsState>,
    private propertySetsHttpService: PropertySetsHttpService
  ) {}

  ngOnInit(): void {
    const stateFilters = this.stateProvider.snapshot().filters;

    this.filters = getFilters()
      .map(this._mapFilterValuesByState(stateFilters))
      .map(f => ({
        ...f,
        hideOptionMenuIcon: true,
        ...(f.associations?.length
          ? { associations: f.associations.map(a => ({ ...a, hideOptionMenuIcon: true })) }
          : {}),
      }));
    this.handleFilterChange(this.filters);

    this.searchCtrl.valueChanges.subscribe(() => this.handleFilterChange(this.filters));
  }

  private _mapFilterValuesByState =
    (state: Record<string, any>) =>
    (filter: Filter): Filter => {
      if (filter.type === FilterTypes.Group) {
        const nextAssociations = filter.associations.map(this._mapFilterValuesByState(state));
        return { ...filter, associations: nextAssociations, values: nextAssociations.map(this._toFilterQuery) };
      }
      const filterValues = state ? state[filter.key] : null;
      return filterValues ? { ...filter, values: filterValues } : filter;
    };

  handleValueChanges(changes: Record<string, any>): void {
    this.filters.forEach(f => {
      if (f.type === FilterTypes.Group) {
        f.values = f.associations.map(a => this._toFilterQuery({ ...a, values: changes[a.key] }));
        return;
      }
      f.values = changes[f.key];
    });

    this.stateProvider.patchState({ filters: changes });

    this._fetchAggregations(this.filters);
  }

  handleFilterChange(changes: Filter[]): void {
    this.filters = changes;
    this._fetchAggregations(changes);
  }

  handleSearchChange(event: KeyboardEvent): void {
    (event.target as HTMLElement).blur();
  }

  private _fetchAggregations(filters: Filter[]): void {
    this._lookupAggregations(filters).subscribe(aggregations => (this.aggregations = aggregations));
  }

  private _lookupAggregations(filters: Filter[]): Observable<Aggregations> {
    const payload = this.generatePayload(filters) as Filter[];
    this.filterChange.emit(payload);
    return this.propertySetsHttpService.getPropertySetLookup(payload).pipe(
      // map to Aggregations
      map(this._toAggregation),
      // handle error
      catchError(this._handleAggregationError)
    );
  }

  private _toAggregation = (data: Record<string, any>) => ({
    markets: data.Market.values.map(this._withoutCount),
    property_types: data.PropertyType.values.map(pt =>
      this._withoutCount({ ...pt, displayName: getPropertyTypeLabel(pt.displayName) })
    ),
    is_statistical: data.Statistical.values.map(this._withoutCount),
    publishing_sector: data.PublishingSector.values.map(this._withoutCount),
    publishing_group: data.PublishingGroup.values.map(this._withoutCount),
    permission_level: data.UserPermissionLevel.values.map(this._withoutCount),
    shared_user_permission: data.SharedPermissionLevel.values.map(this._withoutCount),
  });

  private _withoutCount = (option: Record<string, any>) => ({ ...option, value: null });

  private _handleAggregationError = () => {
    this.filterChange.emit([]);
    return of({ markets: [], property_types: [] } as Aggregations); // Return an empty Aggregations object on error
  };

  openCreateView(): void {
    this.stateProvider.patchState({ id: -1, view: PropertySetView.Details, hasBackButton: true });
  }

  generatePayload(filters = []): Filter[] {
    const ctrlSearchValue = this.searchCtrl.value?.trim() || null;

    const nextFilters: Filter[] = filters.map(this._toFilterQuery);

    return [...nextFilters, { type: FilterTypes.MultiField, values: ctrlSearchValue, dataType: 'text' }].filter(
      f => !this._isValueNull(f.values)
    ) as Filter[];
  }

  private _toFilterQuery = ({ key, type, dataType, field, values }: Filter) => {
    const keyValue = CATALOG_KNOWN_FILTERS[key] != null ? { key: CATALOG_KNOWN_FILTERS[key] } : {};
    return { ...keyValue, type, dataType, field, values } as Filter;
  };

  private _isValueNull = values => {
    if (values == null) return true;

    if (Array.isArray(values)) {
      if (values.length === 0) {
        return true;
      }
      return values.every(val => {
        if (typeof val === 'object') {
          return val.hasOwnProperty('values') ? this._isValueNull(val.values) : this._isValueNull(val);
        } else {
          return this._isValueNull(val);
        }
      });
    }
    if (typeof values === 'object') {
      // If 'values' is an object, check if all its properties are null or undefined
      return Object.values(values).every(this._isValueNull);
    }
    if (typeof values === 'string') {
      return values.trim() === '';
    }
    return false;
  };
}
